import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import {
  Typography,
  Box,
  Paper,
  Link,
  GlobalStyles,
  useMediaQuery,
} from "@mui/material";

import EnvelopeIcon from "../icons/EnvelopeIcon";
import OpenHandIcon from "../icons/OpenHandIcon";
import PencilIcon from "../icons/PencilIcon";
import ArrowRightIcon from "../icons/ArrowRightIcon";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { replaceMainSiteLinkHostname } from "../../services/Utils";

const ThankYouPage = () => {
  const { t } = useTranslation("postChat");
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const CARD_PADDING = { xs: 4, md: 4 };
  const CARD_RADIUS = "16px";
  const CARD_MIN_HEIGHT = { md: 208, lg: 220 };

  useScrollToTop();

  const cardSx = {
    backgroundColor: theme.palette.plum100,
    p: CARD_PADDING,
    borderRadius: CARD_RADIUS,
    minHeight: CARD_MIN_HEIGHT,
    display: "flex",
    flexDirection: "column",
    height: "100%",
  };

  const linkStyles = (theme) => ({
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: 16,
    fontFamily: "Public Sans, sans-serif",
    textDecoration: "underline",
    textUnderlineOffset: "4px",
    textDecorationThickness: "1px",
    outline: "1px dashed transparent",
    p: "1px",
    ":focus": {
      outline: `1px dashed ${theme.palette.secondary.main}`,
    },
    "&:hover": {
      color: theme.palette.tertiary.main,
      textDecorationColor: theme.palette.tertiary.main,
    },
  });

  return (
    <>
      <GlobalStyles
        styles={{
          html: { backgroundColor: "#fff" },
          body: { backgroundColor: "#fff" },
        }}
      />

      <Box
        sx={{
          position: "relative",
          left: "50%",
          right: "50%",
          marginLeft: "-50vw",
          marginRight: "-50vw",
          width: "100vw",
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            px: { xs: 3, sm: 4 },
            pt: { xs: 6, md: 8 },
            pb: { xs: 10, md: 12 },
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: isDesktop ? "31px" : "25px",
              textAlign: "center",
              mb: { xs: 4, md: 6 },
            }}
          >
            {t("thankYouPage.header")}
          </Typography>

          <Box
            sx={{
              display: { xs: "block", md: "grid" },
              gridTemplateColumns: {
                md: "repeat(auto-fit, minmax(256px, 1fr))",
              },
              columnGap: { md: 4 },
              rowGap: { md: 4 },
              "& > * + *": { mt: { xs: 3, sm: 4, md: 0 } },
            }}
          >
            <Paper elevation={0} sx={cardSx}>
              <Box sx={{ mb: 2, lineHeight: 0 }}>
                <OpenHandIcon
                  fill={theme.palette.tertiary.main}
                  sx={{ fontSize: 30 }}
                />
              </Box>

              <Typography
                sx={{
                  fontFamily: "Public Sans, sans-serif",
                  fontWeight: 800,
                  fontSize: 25,
                  lineHeight: "140%",
                  mb: 2,
                }}
              >
                {t("thankYouPage.visitSafeSpaceHeader")}
              </Typography>

              <Typography
                sx={{
                  fontFamily: "Public Sans, sans-serif",
                  fontWeight: 400,
                  fontSize: 16,
                  lineHeight: "180%",
                  color: theme.palette.secondary.main,
                }}
              >
                {t("thankYouPage.safeSpaceDescription")}
              </Typography>

              <Box sx={{ mt: "auto" }}>
                <Link
                  href={t("thankYouPage.safeSpaceLink")}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="always"
                  sx={linkStyles(theme)}
                >
                  {t("thankYouPage.safeSpaceText")}
                  <ArrowRightIcon sx={{ fontSize: 25, pl: 1, pb: "1px" }} />
                </Link>
              </Box>
            </Paper>

            <Paper elevation={0} sx={cardSx}>
              <Box sx={{ mb: 2, lineHeight: 0 }}>
                <EnvelopeIcon
                  fill={theme.palette.tertiary.main}
                  sx={{ fontSize: 30 }}
                />
              </Box>

              <Typography
                sx={{
                  fontFamily: "Public Sans, sans-serif",
                  fontWeight: 800,
                  fontSize: 25,
                  lineHeight: "140%",
                  mb: 2,
                }}
              >
                {t("thankYouPage.kindMindHeader")}
              </Typography>

              <Typography
                sx={{
                  fontFamily: "Public Sans, sans-serif",
                  fontWeight: 400,
                  fontSize: 16,
                  lineHeight: "180%",
                  color: theme.palette.secondary.main,
                }}
              >
                {t("thankYouPage.kindMindDescription")}
              </Typography>

              <Box sx={{ mt: "auto" }}>
                <Link
                  href={t("thankYouPage.kindMindLink")}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="always"
                  sx={linkStyles(theme)}
                >
                  {t("thankYouPage.kindMindLinkText")}
                  <ArrowRightIcon sx={{ fontSize: 25, pl: 1, pb: "1px" }} />
                </Link>
              </Box>
            </Paper>

            <Paper elevation={0} sx={cardSx}>
              <Box sx={{ mb: 2, lineHeight: 0 }}>
                <PencilIcon
                  fill={theme.palette.tertiary.main}
                  sx={{ fontSize: 30 }}
                />
              </Box>

              <Typography
                sx={{
                  fontFamily: "Public Sans, sans-serif",
                  fontWeight: 800,
                  fontSize: 25,
                  lineHeight: "140%",
                  mb: 2,
                }}
              >
                {t("thankYouPage.contactUsHeader")}
              </Typography>

              <Typography
                sx={{
                  fontFamily: "Public Sans, sans-serif",
                  fontWeight: 400,
                  fontSize: 16,
                  lineHeight: "180%",
                  color: theme.palette.secondary.main,
                }}
              >
                {t("thankYouPage.contactUsDescription")}
              </Typography>

              <Box sx={{ mt: "auto" }}>
                <Link
                  href={replaceMainSiteLinkHostname(
                    t("thankYouPage.contactUsPageLink"),
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="always"
                  sx={linkStyles(theme)}
                >
                  {t("thankYouPage.contactUsPageLinkText")}
                  <ArrowRightIcon sx={{ fontSize: 25, pl: 1, pb: "1px" }} />
                </Link>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ThankYouPage;
